import React from "react";
import { Box, Stack, Badge, Text, useColorModeValue } from "@chakra-ui/react";
import dayjs from "dayjs";
import DetailInfoRow from "./DetailInfoRow";
import LegalStatusBadge from "./LegalStatusBadge";
import { ROLE_LABELS } from "./legalDocumentConstants";

/**
 * Renders the status, language, and version rows of the metadata panel.
 * @param {object} props
 * @param {object} props.doc - The legal document object
 * @returns {React.ReactElement}
 */
function MetadataTopRows({ doc }) {
    const valueColor = useColorModeValue("secondaryGray.900", "whiteAlpha.900");
    return (
        <>
            <DetailInfoRow label="Version" value={
                <Text fontSize="0.85rem" fontWeight="600" color={valueColor}>{doc.version}</Text>} />
            <DetailInfoRow label="Status" value={<LegalStatusBadge status={doc.status} />} />
            <DetailInfoRow label="Language" value={
                <Badge bg="rgba(33,150,243,0.15)" color="#2196f3" borderRadius="full" px="8px"
                    fontSize="0.7rem" fontWeight="600">{doc.language?.toUpperCase()}</Badge>} />
        </>
    );
}

/**
 * Renders the platforms info row with badge tags for each platform.
 * @param {object} props
 * @param {Array<string>} props.platforms - List of platform names
 * @returns {React.ReactElement}
 */
function PlatformsRow({ platforms }) {
    const tagColor = useColorModeValue("secondaryGray.700", "secondaryGray.300");
    return (
        <DetailInfoRow label="Platforms" value={
            <Stack direction="row" spacing="4px">
                {(platforms || []).map((p, i) => (
                    <Badge key={i} bg="whiteAlpha.200" color={tagColor} borderRadius="6px" px="6px"
                        fontSize="0.7rem" textTransform="none">{p}</Badge>
                ))}
            </Stack>} />
    );
}

/**
 * Renders the parent document ID info row.
 * @param {object} props
 * @param {number|string} props.parentId - The parent document ID
 * @returns {React.ReactElement}
 */
function ParentIdRow({ parentId }) {
    const tagColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <DetailInfoRow label="Parent ID" value={
            <Badge bg="whiteAlpha.200" color={tagColor} borderRadius="6px" px="6px"
                fontSize="0.7rem" fontFamily="mono">{`#${parentId}`}</Badge>} />
    );
}

/**
 * Renders the full metadata panel for a legal document, including
 * version, status, language, target role, platforms, dates, and parent ID.
 * @param {object} props
 * @param {object} props.doc - The legal document object
 * @returns {React.ReactElement}
 */
function DetailMetadataPanel({ doc }) {
    const boxBg = useColorModeValue("secondaryGray.100", "whiteAlpha.100");
    const valueColor = useColorModeValue("secondaryGray.800", "secondaryGray.300");
    const dateColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box p="16px" borderRadius="14px" mb="16px" bg={boxBg}>
            <MetadataTopRows doc={doc} />
            <DetailInfoRow label="Target" value={
                <Text fontSize="0.8rem" color={valueColor}>{ROLE_LABELS[doc.target_role] || doc.target_role}</Text>} />
            <PlatformsRow platforms={doc.platforms} />
            <DetailInfoRow label="Effective date" value={
                <Text fontSize="0.8rem" color={valueColor}>
                    {doc.effective_date ? dayjs(doc.effective_date).format("DD/MM/YYYY") : "—"}
                </Text>} />
            {doc.parent_id && <ParentIdRow parentId={doc.parent_id} />}
            <DetailInfoRow label="Created" value={
                <Text fontSize="0.78rem" color={dateColor}>{dayjs(doc.created_at).format("DD/MM/YYYY HH:mm")}</Text>} />
        </Box>
    );
}

export default DetailMetadataPanel;
